/**
 * Keeps password-manager extensions out of ordinary record/form fields (VALE-1772).
 *
 * **The bug.** 1Password (and the same class of extension — LastPass, Bitwarden,
 * Dashlane, Proton Pass) treats a cluster of short text inputs near a "Save"/"Done"
 * action as an identity-creation form, regardless of whether any field is
 * `type="password"`. Reported on a contact's First/Last name popover: 1Password
 * painted its inline icon in the field and offered "Save in 1Password" over the
 * panel. Accepting that offer writes a client's name into a personal password
 * vault, and a later autofill can type one client's details into another
 * client's record.
 *
 * **Each vendor ships its own attribute, and there is no standard one.**
 * `autoComplete="off"` alone is deliberately not honoured by any of them — sites
 * used it to defeat password managers, so the vendors stopped honouring it
 * (documented 1Password policy; the same reasoning Chromium gives for ignoring it
 * on password fields). Citations: 1Password —
 * developer.1password.com/docs/web/compatible-website-design (`data-1p-ignore`,
 * boolean, on the field or on `<body>` for a whole page); LastPass —
 * `data-lpignore` (needs the literal `="true"`, a bare boolean may not register);
 * Bitwarden — `data-bwignore` (community-documented, consistent across sources,
 * no official vendor docs found); Dashlane — `data-form-type="other"` (their own
 * "Semantically Annotated Web Forms" spec, a different mechanism to the same
 * effect); Proton Pass — `data-protonpass-ignore`.
 *
 * **Keeper and Safari are deliberately NOT covered here.** Keeper has no
 * documented per-field HTML attribute — a community-referenced `class`
 * (`keeper-ignore`) is not in Keeper's own docs, so nothing is bound to it.
 * Safari has no reliable per-field opt-out at all (Apple's own engineers have
 * said so in public W3C discussion); it is user-controlled only (iOS Settings),
 * which no amount of markup on our side can change. Both are genuine gaps, not
 * oversights — see VALE-1772's research comment.
 *
 * **`autoComplete` is deliberately excluded from this set.** The vendor `data-*`
 * attributes above only suppress a password-manager *extension's* save/fill UI —
 * a separate subsystem from the browser's own general autofill (name, address).
 * `autoComplete="off"`, by contrast, DOES suppress the browser's own autofill in
 * current Chromium (any non-"on" value does). The public client-intake portal
 * (`app/(public)/portal/`, `components/client-intake/`) renders ordinary text
 * inputs too, and there the browser's own autofill of the client's own name and
 * address is wanted — so this constant reaches those fields (a password manager
 * can still misclassify them) without breaking that.
 *
 * **Applied as the DEFAULT on `components/ui/input.tsx` and
 * `components/ui/textarea.tsx`, not spread field-by-field.** That is the "single
 * shared place" a field added later inherits automatically. Both primitives skip
 * the default for `type="password"` — a password-type field is a genuine secret
 * (the account API-keys card is the one example in this codebase today), and a
 * password manager offering to remember it is the wanted behaviour, not the bug.
 * A raw `<input>` that renders outside those two primitives (a handful of
 * hand-rolled inline editors — see `__tests__/autofill-opt-out-registry.test.ts`)
 * spreads this constant explicitly, since it cannot inherit a default it never
 * calls into.
 */
export const AUTOFILL_OPT_OUT = {
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-bwignore': true,
  'data-form-type': 'other',
  'data-protonpass-ignore': true
} as const;
